import { getAllBindingTargetNames } from '../../builtin/bindingNode'
import type { ForNode, LoopBindingNode } from '../../builtin/specialExpressions/loops'
import { NodeTypes } from '../../constants/constants'
import { ParseError } from '../../errors'
import type { AstNode, BindingTarget } from '../types'
import { bindingTargetTypes } from '../types'
import type { SourceCodeInfo, SymbolToken, Token } from '../../tokenizer/token'
import { asSymbolToken, assertLParenToken, assertOperatorToken, assertRParenToken, assertReservedSymbolToken, isOperatorToken, isRParenToken, isReservedSymbolToken, isSymbolToken } from '../../tokenizer/token'
import { asUserDefinedSymbolNode } from '../../typeGuards/astNode'
import { withSourceCodeInfo } from '../helpers'
import type { ParserContext } from '../ParserContext'
import { parseLet } from './parseLet'
import { parseSymbol } from './parseSymbol'

type InternalLoopBindingDelimiter = 'let' | 'when' | 'while'

export function parseForOrDoseq(ctx: ParserContext, firstToken: SymbolToken): ForNode {
  ctx.builder?.startNode('For')
  ctx.advance()

  assertLParenToken(ctx.tryPeek())
  ctx.advance()

  const forLoopBindings: LoopBindingNode[] = []

  while (!ctx.isAtEnd() && !isRParenToken(ctx.tryPeek())) {
    const loopBinding = parseForLoopBinding(ctx)
    const existingBoundNames = forLoopBindings.flatMap(b => Object.keys(getAllBindingTargetNames(b[0][0])))
    const newBoundNames = getAllBindingTargetNames(loopBinding[0][0])
    if (Object.keys(newBoundNames).some(n => existingBoundNames.includes(n))) {
      throw new ParseError('Duplicate binding', undefined)
    }
    forLoopBindings.push(loopBinding)
    if (isOperatorToken(ctx.tryPeek(), ',')) {
      ctx.advance()
    }
  }

  assertRParenToken(ctx.tryPeek())
  ctx.advance()

  assertOperatorToken(ctx.tryPeek(), '->')
  ctx.advance()

  const expression = ctx.parseExpression()

  const node = withSourceCodeInfo([NodeTypes.For, [forLoopBindings, expression], 0], firstToken[2], ctx) as ForNode
  ctx.setNodeEnd(node[2])
  ctx.builder?.endNode()
  return node
}

function parseForLoopBinding(ctx: ParserContext): LoopBindingNode {
  const bindingNode = parseBinding(ctx)

  const modifiers: ('&let' | '&when' | '&while')[] = []
  let token = ctx.peek()

  assertInternalLoopBindingDelimiter(token, ['let', 'when', 'while'], ctx.peekSourceCodeInfo())

  const letBindings: [BindingTarget, AstNode][] = []
  if (token[1] === 'let') {
    modifiers.push('&let')
    while (isSymbolToken(token, 'let')) {
      const letNode = parseLet(ctx, token)
      const existingBoundNames = letBindings.flatMap(b => Object.keys(getAllBindingTargetNames(b[0])))
      const [letTarget, letValue] = letNode[1] as [BindingTarget, AstNode]
      const newBoundNames = Object.keys(getAllBindingTargetNames(letTarget))
      if (newBoundNames.some(n => existingBoundNames.includes(n))) {
        throw new ParseError('Duplicate binding', undefined)
      }

      letBindings.push([letTarget, letValue])
      token = ctx.peek()
      assertInternalLoopBindingDelimiter(token, ['let', 'when', 'while'], ctx.peekSourceCodeInfo())
      token = ctx.peek()
    }
  }

  let whenNode: AstNode | undefined
  let whileNode: AstNode | undefined
  while (
    isReservedSymbolToken(token, 'when')
    || isReservedSymbolToken(token, 'while')
  ) {
    ctx.advance()

    if (token[1] === 'when') {
      modifiers.push('&when')
      whenNode = ctx.parseExpression()
    } else {
      modifiers.push('&while')
      whileNode = ctx.parseExpression()
    }
    token = ctx.peek()

    const symbols: ('when' | 'while')[] = modifiers.includes('&when') && modifiers.includes('&while')
      ? []
      : modifiers.includes('&when')
        ? ['while']
        : ['when']

    assertInternalLoopBindingDelimiter(token, symbols, ctx.peekSourceCodeInfo())
    token = ctx.peek()
  }

  assertInternalLoopBindingDelimiter(token, [], ctx.peekSourceCodeInfo())

  return [bindingNode, letBindings, whenNode, whileNode] satisfies LoopBindingNode
}

function parseBinding(ctx: ParserContext): [BindingTarget, AstNode] {
  const firstToken = asSymbolToken(ctx.tryPeek())
  const name = asUserDefinedSymbolNode(parseSymbol(ctx))

  assertReservedSymbolToken(ctx.tryPeek(), 'in')
  ctx.advance()

  const value = ctx.parseExpression()

  const target: BindingTarget = withSourceCodeInfo([bindingTargetTypes.symbol, [name, undefined], 0], firstToken[2], ctx)
  ctx.setNodeEnd(target[2])
  return [target, value]
}

function assertInternalLoopBindingDelimiter(token: Token, symbols: InternalLoopBindingDelimiter[], sourceCodeInfo?: SourceCodeInfo): void {
  if (!isInternalLoopBindingDelimiter(token, symbols)) {
    const symbolsString = `${[...symbols, ','].map(symbol => `"${symbol}"`).join(', ')} or ")"`
    throw new ParseError(`Expected symbol ${symbolsString}`, sourceCodeInfo)
  }
}

function isInternalLoopBindingDelimiter(token: Token, symbols: InternalLoopBindingDelimiter[]): boolean {
  // end of loop binding
  if (isOperatorToken(token, ',') || isRParenToken(token)) {
    return true
  }
  for (const symbol of symbols) {
    if (symbol === 'let' && isSymbolToken(token, 'let')) {
      return true
    }
    if (['when', 'while'].includes(symbol) && isReservedSymbolToken(token, symbol as 'when' | 'while')) {
      return true
    }
  }
  return false
}
